import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DashboardLayout, GetDefaultDashboardLayout } from 'src/app/dashboards/models';
import { getInsightsPbiChartType } from '../../chart/chart';
import { DashboardPreviewDetailsDto, DashboardType, DashboardVisualDetailsDto } from '../../../services/api.services';
import { logger } from '../../../pbir/utils/logger';

export enum DashboardPreviewColor {
  Default = '',
  Alt = 'alt',
  AltSelected = 'alt__selected'
};

const LOGGER = logger('DashboardPreviewComponent');

@Component({
  selector: 'app-dashboard-preview',
  templateUrl: './dashboard-preview.component.html',
  styleUrls: ['./dashboard-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DashboardPreviewComponent implements OnChanges {
  @Input() dashboard: DashboardPreviewDetailsDto;

  @Input() iconSize: string | number= 36;
  @Input() smallIconSize: string | number = 20;

  @Input() displayNames = true;
  @Input() nameMargin: string | number = 35;

  @Input() padding: string | number = 0;
  @Input() columnGap: string | number = 6;
  @Input() rowGap: string | number = 6;

  @Input() color: string = DashboardPreviewColor.Default;

  pbiDashboard = false;

  layout: DashboardLayout;
  invalidLayout = false;
  emptyLayout = false;

  iconSizeStyle: string = '';
  smallIconSizeStyle: string = '';
  nameMarginStyle: string = '';
  gridStyle: string = '';
  gridElements: GridElement[] = [];

  constructor() { }

  ngOnInit() {
    this.setIconSizeStyles();
    this.setNameMarginStyle();
    this.setGridStyle();
  }

  ngOnChanges(changes: SimpleChanges) {
    let setIconSizeStyles = false;
    let setNameMarginStyle = false;
    let setGridStyle = false;
    let setGridElements = false;

    if (changes.dashboard && this.dashboard) {
      if (this.dashboard.type === DashboardType.PowerBiReport) {
        this.pbiDashboard = true;
      }
      else {
        this.pbiDashboard = false;

        this.parseLayout();

        this.emptyLayout = this.layout.visuals.length === 0 || this.dashboard.visuals.length === 0;

        setGridStyle = true;
        setGridElements = true;
      }
    }

    if (!this.pbiDashboard) {
      if (changes.nameMargin) {
        setNameMarginStyle = true;
      }

      if (changes.columnGap || changes.rowGap) {
        setGridStyle = true;
      }
    }

    if (changes.iconSize || changes.smallIconSize)
      setIconSizeStyles = true;

    if (setIconSizeStyles)
      this.setIconSizeStyles();

    if (setNameMarginStyle)
      this.setNameMarginStyle();

    if (setGridStyle)
      this.setGridStyle();

    if (setGridElements)
      this.setGridElements();
  }

  private parseLayout() {
    this.layout = GetDefaultDashboardLayout();

    try {
      let parsedLayout = JSON.parse(this.dashboard.layout);

      this.layout = { ...this.layout, ...parsedLayout };

      this.invalidLayout = false;
    }
    catch (error) {
      LOGGER.error(error);

      this.invalidLayout = true;
    }
  }

  private setIconSizeStyles() {
    let sizeValue = this.valueOrPixels(this.iconSize);
    let smallSizeValue = this.valueOrPixels(this.smallIconSize);

    this.iconSizeStyle = `width: ${sizeValue}; height: ${sizeValue}; flex-basis: ${sizeValue};`;
    this.smallIconSizeStyle = `width: ${smallSizeValue}; height: ${smallSizeValue}; flex-basis: ${smallSizeValue};`;
  }

  private setNameMarginStyle() {
    this.nameMarginStyle = `flex-basis: ${this.valueOrPixels(this.nameMargin)}`;
  }

  private setGridStyle() {
    if (this.invalidLayout || !this.layout)
      return;

    let rows = this.layout.pageHeight ?? this.layout.rows;

    this.gridStyle = `
      grid-template-columns: repeat(${this.layout.columns}, [col-start] 1fr);
      grid-template-rows: repeat(${rows}, [row-start] 1fr);

      column-gap: ${this.valueOrPixels(this.columnGap)};
      row-gap: ${this.valueOrPixels(this.rowGap)};
      `;
  }

  private setGridElements() {
    this.gridElements = [];

    let altColors = this.color == DashboardPreviewColor.Alt || this.color == DashboardPreviewColor.AltSelected;

    if (this.invalidLayout || !this.layout)
      return;

    let rows = this.layout.pageHeight ?? this.layout.rows;

    for (let i = 0; i < this.layout.visuals.length; ++i) {
      let visualLayout = this.layout.visuals[i];
      let visual = this.dashboard.visuals[i];

      // INFO: Preview does not support visuals that go outside the layout
      if (visualLayout.x >= this.layout.columns || visualLayout.y >= rows)
        continue;

      let visibleSizeX = Math.min(visualLayout.x + visualLayout.sizeX, this.layout.columns) - visualLayout.x;
      let visibleSizeY = Math.min(visualLayout.y + visualLayout.sizeY, rows) - visualLayout.y;

      let xSizeScale = visibleSizeX / this.layout.columns;
      let ySizeScale = visibleSizeY / rows;

      let chartType = getInsightsPbiChartType(visual?.type);

      let classes = [];

      if (visual?.deleted) {
        classes.push('dashboard-preview__grid-element__deleted');
      }
      else if (visual?.unshared) {
        classes.push('dashboard-preview__grid-element__unshared');
      }

      this.gridElements.push({
        name: visual?.name,
        iconImageUrl: this.selectChartTypeImageUrl(altColors, visual),
        smallIcon: xSizeScale < 0.26 || ySizeScale < 0.26,
        style: `
          grid-column-start: ${visualLayout.x + 1};
          grid-column-end: span ${visibleSizeX};
          grid-row-start: ${visualLayout.y + 1};
          grid-row-end: span ${visibleSizeY};
          `,
        textStyle: `
          -webkit-line-clamp: ${visibleSizeY < 2 ? 2 : 4};
          ${visibleSizeY < 2 ? 'margin-top: -30px;' : ''}
        `,
        classes: classes.join(' ')
      });
    }
  }

  private selectChartTypeImageUrl(altColors: boolean, visual: DashboardVisualDetailsDto) {
    let chartType = getInsightsPbiChartType(visual?.type);

    if (visual?.deleted)
      return chartType.previewRedImageUrl;

    if (visual?.unshared)
      return chartType.previewGreyImageUrl;

    if (altColors)
      return chartType.previewWhiteImageUrl;

    return chartType.previewImageUrl;
  }

  private valueOrPixels(value: string | number) {
    if (typeof value == 'number' || /^\s*[0-9]+\s*$/.test(value)) {
      return value + 'px';
    }

    return value;
  }
}

interface GridElement {
  name: string;
  iconImageUrl: string;
  smallIcon: boolean;
  style: string;
  textStyle: string;
  classes: string;
};
